import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { App } from '@capacitor/app';
import { ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, firstValueFrom, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { MySocialService } from '../social/my-social.service';
import { AddressStatus, REFERRAL_CODE_WAITING_KEY, isAskedToConfirm } from './an-address-waits-for-its-confirmation';

/** The page « Tu n'as rien reçu ? », where the link is sent again. */
export const RESEND_PAGE = '/email-resend';

/**
 * What the application knows of the confirmation of the address, and the
 * three calls it makes about it (Asana 1218325456154102).
 *
 * New routes: the API answers the object itself, not an Either. Each address
 * is written out whole, so that the guard on the released API reads all three.
 *
 * The status is read once the member is known, and read again each time the
 * app comes back to the foreground while the person is asked: that is when
 * they come back from their inbox. Nobody else costs a call on resume.
 */
@Injectable({ providedIn: 'root' })
export class EmailConfirmationService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly toasts = inject(ToastController);
  private readonly translate = inject(TranslateService);
  private readonly social = inject(MySocialService);
  private started = false;

  /** Undefined until the API answered, and when it could not: read as « not asked ». */
  readonly status$ = new BehaviorSubject<AddressStatus | undefined>(undefined);

  /** Called once the member is known. Calling it again does nothing. */
  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    void this.refresh();
    void App.addListener('resume', () => {
      if (isAskedToConfirm(this.status$.value)) {
        void this.check();
      }
    });
  }

  /** Never throws: an API without the route leaves the person not asked. */
  async refresh(): Promise<AddressStatus | undefined> {
    try {
      const status = await firstValueFrom(this.http.get<AddressStatus>(`${environment.api}/api/v1/email-confirmation`));
      this.status$.next(status);
      if (status.confirmed) {
        // Confirmed while the app was closed: the return was never seen, the
        // code that waited is claimed now. The claim settles the referral.
        await this.claimTheWaitingCode();
      }
      return status;
    } catch {
      return this.status$.value;
    }
  }

  /**
   * Right after sign-up: the API sends the link on its bus, so the account
   * exists a moment before the email has left, and the person is asked only
   * once it has. A few readings a little apart, then the answer as it stands.
   * Still not asked (the template ids not pasted, SendGrid down) means the
   * screen « Confirme ton adresse » has nothing true to say, and is skipped.
   */
  async waitUntilAsked(
    tries = 4,
    pause: () => Promise<void> = () => new Promise((resolve) => setTimeout(resolve, 800)),
  ): Promise<AddressStatus | undefined> {
    let status: AddressStatus | undefined;
    for (let i = 0; i < tries; i++) {
      if (i > 0) {
        await pause();
      }
      status = await this.refresh();
      if (isAskedToConfirm(status) || status?.confirmed) {
        return status;
      }
    }
    return status;
  }

  /** Sends the link again, to a corrected address when one is given. Throws what the API refused. */
  send(email?: string): Observable<AddressStatus> {
    return this.http
      .post<AddressStatus>(`${environment.api}/api/v1/email-confirmation/send`, email === undefined ? {} : { email })
      .pipe(tap((status) => this.status$.next(status)));
  }

  /**
   * Asks the API whether the address is confirmed now. When it just became
   * so: a message, not a whole screen (Maude, 28 October 2026), and the
   * referral code that waited for it is claimed.
   */
  async check(): Promise<void> {
    const before = this.status$.value;
    let status: AddressStatus;
    try {
      status = await firstValueFrom(
        this.http.post<AddressStatus>(`${environment.api}/api/v1/email-confirmation/check`, {}),
      );
    } catch {
      return;
    }
    this.status$.next(status);
    if (isAskedToConfirm(before) && status.confirmed) {
      await this.say('EMAIL_CONFIRMATION.CONFIRMED', 'checkmark-outline');
      await this.claimTheWaitingCode();
    }
  }

  /** A referral code refused for the address, kept to be claimed once it is confirmed. */
  keepForLater(code: string): void {
    try {
      localStorage.setItem(REFERRAL_CODE_WAITING_KEY, code);
    } catch {
      // Nothing to do: the reward is the inviter's, and the person can be told.
    }
  }

  /**
   * The message of the three actions that wait for the address: what
   * happened, and the way to the link. Longer than the usual toast, because
   * it asks for something.
   */
  async tellTheActionWaits(
    key = 'EMAIL_CONFIRMATION.ACTION_WAITS',
    // A form shown in a modal closes first, or the page opens behind it.
    beforeLeaving: () => unknown = () => undefined,
  ): Promise<void> {
    const toast = await this.toasts.create({
      message: this.translate.instant(key),
      duration: 8000,
      icon: 'mail-outline',
      position: 'top',
      // Stacked: beside the message, the button squeezed it to a word a line.
      layout: 'stacked',
      cssClass: 'toast-with-link',
      buttons: [
        {
          text: this.translate.instant('EMAIL_CONFIRMATION.RESEND_THE_LINK'),
          handler: async () => {
            await beforeLeaving();
            await this.router.navigateByUrl(RESEND_PAGE);
          },
        },
      ],
    });
    await toast.present();
  }

  private async claimTheWaitingCode(): Promise<void> {
    let code: string | null = null;
    try {
      code = localStorage.getItem(REFERRAL_CODE_WAITING_KEY);
    } catch {
      return;
    }
    if (!code) {
      return;
    }
    try {
      await firstValueFrom(this.social.claim(code));
    } catch (error) {
      if ((error as { status?: number })?.status === 0) {
        // No network: kept, the next return to the app tries again.
        return;
      }
      // Refused now (already claimed, the switch turned off): it would be
      // refused the next time too, so it is not kept.
    }
    try {
      localStorage.removeItem(REFERRAL_CODE_WAITING_KEY);
    } catch {
      // Nothing to do.
    }
  }

  private async say(key: string, icon: string): Promise<void> {
    const toast = await this.toasts.create({
      message: this.translate.instant(key),
      duration: 3000,
      icon,
      position: 'top',
    });
    await toast.present();
  }
}
